import { useMemo } from 'react';
import { icsFormsForFamily } from 'components/IAP/ICSConstants';
import type { IcsFormFamily } from 'components/IAP/formFamily';
import { PREMIUM_RESOURCE_WIDGETS } from 'components/IAP/premiumResourceManagement';
import {
  buildFormsDropdownOptions,
  FormsDropdown,
} from 'components/IAP/planningView/widgetRegistry';
import { usePremiumResourceManagementFlag } from 'utils/featureFlags';
import type { PlanLibraryForm } from 'hooks/planLibrary/planLibraryHooks';
import { widgetNameForIcsForm } from './planFormWidget';

export function blankPlanForm(
  formName: string,
  label: string,
  family: IcsFormFamily
): PlanLibraryForm {
  return {
    id: crypto.randomUUID(),
    formName,
    label,
    widgetName: widgetNameForIcsForm(formName),
    family,
    payload: { fields: {} },
  };
}

const PLAN_FORM_EXCLUDED_WIDGETS = ['agenda', 'initialResponse'];

/** Widget name to the ICS form it stands for, Coast Guard editions first. */
function planFormsByWidget(): Map<
  string,
  { formName: string; label: string; family: IcsFormFamily }
> {
  const byWidget = new Map<
    string,
    { formName: string; label: string; family: IcsFormFamily }
  >();
  for (const family of ['uscg', 'fema'] as const) {
    for (const form of icsFormsForFamily(family)) {
      const widget = widgetNameForIcsForm(form.formName);
      if (!byWidget.has(widget)) {
        byWidget.set(widget, {
          formName: form.formName,
          label: form.label,
          family,
        });
      }
    }
  }
  return byWidget;
}

/** The incident-workspace Forms picker, limited to forms a plan can carry. */
export function PlanFormsDropdown({
  onPick,
}: {
  onPick: (form: PlanLibraryForm) => void;
}) {
  const premium = usePremiumResourceManagementFlag();
  const byWidget = useMemo(() => planFormsByWidget(), []);
  const options = useMemo(
    () =>
      buildFormsDropdownOptions({
        exclude: premium
          ? PLAN_FORM_EXCLUDED_WIDGETS
          : [...PLAN_FORM_EXCLUDED_WIDGETS, ...PREMIUM_RESOURCE_WIDGETS],
      }).filter((option) => byWidget.has(option.value)),
    [byWidget, premium]
  );

  return (
    <FormsDropdown
      id="plan-library-forms-dropdown"
      options={options}
      value=""
      onChange={(widgetName) => {
        const form = byWidget.get(widgetName);
        if (!form) return;
        onPick(blankPlanForm(form.formName, form.label, form.family));
      }}
    />
  );
}
